import { Clipboard, HardDrive, KeyRound, SlidersHorizontal } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSession } from "../App.js";
import { api } from "../lib/api.js";
import { copyBackupToClipboard } from "../lib/BackupBanner.js";
import {
  importGroupKey,
  isBackupConfirmed,
  loadGroupKey,
  markBackupConfirmed,
} from "../lib/keys.js";

interface StorageUsage {
  bytesStored: number;
  alertThresholdBytes: number;
  targetCeilingBytes: number;
}

const GB = 1024 ** 3;
const gb = (n: number) => (n / GB).toFixed(2);

function StorageUsageCard() {
  const [usage, setUsage] = useState<StorageUsage | null>(null);
  const [editing, setEditing] = useState(false);
  const [alertGb, setAlertGb] = useState("6");
  const [ceilingGb, setCeilingGb] = useState("10");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = () =>
    api<{ usage: StorageUsage }>("/v1/storage/usage")
      .then((r) => {
        setUsage(r.usage);
        setAlertGb(gb(r.usage.alertThresholdBytes));
        setCeilingGb(gb(r.usage.targetCeilingBytes));
      })
      .catch((e) => setErr((e as Error).message));
  useEffect(() => {
    void load();
  }, []);

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      await api("/v1/storage/settings", {
        body: {
          alertThresholdBytes: Number(alertGb) * GB,
          targetCeilingBytes: Number(ceilingGb) * GB,
        },
      });
      await load();
      setEditing(false);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!usage) return null;
  const overThreshold = usage.bytesStored >= usage.alertThresholdBytes;
  const pct = Math.min(100, (usage.bytesStored / usage.targetCeilingBytes) * 100);
  return (
    <div className={`card ${overThreshold ? "warn" : ""}`}>
      <div className="row" style={{ justifyContent: "space-between" }}>
        <h2 style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
          <HardDrive size={18} className="icon" />
          Storage usage
        </h2>
        {overThreshold && <span className="badge warn">approaching your limit</span>}
      </div>
      <p style={{ margin: "8px 0" }}>
        {gb(usage.bytesStored)} GB of {gb(usage.targetCeilingBytes)} GB
      </p>
      <div
        style={{
          height: 8,
          borderRadius: 999,
          background: "var(--line)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${pct}%`,
            background: overThreshold ? "var(--warn)" : "var(--accent)",
          }}
        />
      </div>
      {overThreshold && (
        <p className="muted" style={{ marginTop: 10 }}>
          Nothing is deleted automatically. If you want to free up space, open a vault and use its
          "Danger zone" to delete recordings you no longer need.
        </p>
      )}
      {!editing ? (
        <button
          style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 6 }}
          onClick={() => setEditing(true)}
        >
          <SlidersHorizontal size={15} />
          Change thresholds
        </button>
      ) : (
        <div className="row" style={{ marginTop: 10 }}>
          <div>
            <label>Alert at (GB)</label>
            <input
              style={{ maxWidth: 100 }}
              value={alertGb}
              onChange={(e) => setAlertGb(e.target.value)}
            />
          </div>
          <div>
            <label>Target ceiling (GB)</label>
            <input
              style={{ maxWidth: 100 }}
              value={ceilingGb}
              onChange={(e) => setCeilingGb(e.target.value)}
            />
          </div>
          <button className="primary" style={{ alignSelf: "end" }} onClick={save} disabled={busy}>
            Save
          </button>
        </div>
      )}
      {err && <p className="error">{err}</p>}
    </div>
  );
}

function KeyBackup({ userId }: { userId: string }) {
  const [pasted, setPasted] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(() => isBackupConfirmed(userId));
  const [copied, setCopied] = useState(false);
  const has = !!loadGroupKey(userId);
  async function copy() {
    const shown = await copyBackupToClipboard(userId);
    setCopied(true);
    setMsg(
      shown ??
        "Copied. Paste it into your password manager now (a secure note or password entry), then confirm below.",
    );
  }
  function confirmSaved() {
    markBackupConfirmed(userId);
    setConfirmed(true);
  }
  function restore() {
    try {
      importGroupKey(userId, pasted.trim());
      setMsg("Key restored. Reload the page.");
      setPasted("");
    } catch {
      setMsg("That does not look like a Tether decryption key.");
    }
  }
  return (
    <div className="card">
      <h2 style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <KeyRound size={18} className="icon" />
        Your decryption key
      </h2>
      <p className="muted">
        Your decryption key opens what is sealed to you. It lives only in this browser and is
        separate from your passkey, which only signs you in. Back it up so a new browser, a cleared
        profile, or a closed private window does not lock you out. Clipboard managers can retain
        what you copy, so paste it into a password manager right away and don't leave it sitting in
        a plain note. <Link to="/guide#lost">What if I lose it?</Link>
      </p>
      <p>
        <span className={`badge ${has ? "ok" : "bad"}`}>
          {has ? "present in this browser" : "missing in this browser"}
        </span>{" "}
        {has && (
          <span className={`badge ${confirmed ? "ok" : "warn"}`}>
            {confirmed ? "backup saved" : "backup not confirmed"}
          </span>
        )}
      </p>
      <div className="row">
        <button
          onClick={copy}
          disabled={!has}
          style={{ display: "flex", alignItems: "center", gap: 6 }}
        >
          <Clipboard size={15} />
          Copy backup to clipboard
        </button>
        {has && !confirmed && (
          <button className="primary" onClick={confirmSaved} disabled={!copied}>
            I've saved it in my password manager
          </button>
        )}
        <input
          style={{ maxWidth: 320 }}
          placeholder="Paste a backup to restore"
          value={pasted}
          onChange={(e) => setPasted(e.target.value)}
        />
        <button className="primary" onClick={restore} disabled={!pasted}>
          Restore
        </button>
      </div>
      {msg && (
        <p className="muted" style={{ wordBreak: "break-all" }}>
          {msg}
        </p>
      )}
    </div>
  );
}

export function Settings() {
  const { me } = useSession();
  if (!me) return null;
  return (
    <div>
      <h2 style={{ margin: "0 0 12px", display: "flex", alignItems: "center", gap: 8 }}>
        <SlidersHorizontal size={20} className="icon" />
        Settings
      </h2>
      {me.user.role === "owner" && <StorageUsageCard />}
      <KeyBackup userId={me.user.id} />
    </div>
  );
}
